'use client';

import TitleBar from "../../components/title-bar";
import Image from 'next/image';
import iconMiaobiaoImg from '@/features/home/images/icon-miaobiao.png';
import { useTranslations } from 'next-intl';
import { CardRummyItem } from "../../services/home";
import { track } from "@/utils/track";
import useFn from "@/hooks/use-fn";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCountDown, useInViewport } from "ahooks";
import { useAtom, useAtomValue } from "jotai";
import { freeTimesAtom, isRummyLoadedOnceAtom, rummyGameUrlAtom, rummySubPageAtom } from "@/app/[locale]/(main)/(non-activity)/rummy/store";
import { RummySubPage } from "@/app/[locale]/(main)/(non-activity)/rummy/const";
import { useRouter } from "@/i18n/navigation";
import { menuTabUrl } from "@/const/page-url";
import { useRummyRecommend } from "../../services/rummy";
import { useRouteActive } from '@/components/layout/route-keep-alive';
import { Spin } from "@/components/ui-feat/spin";
import RummyCardWithExposure from "./components/rummy-card-with-exposure";

export default function Rummy() {
  const { isActive } = useRouteActive();
  // 视口检测：用于懒加载图片资源
  const containerRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(containerRef, {
    rootMargin: '100px 0px', // 提前100px开始加载，提升用户体验
  });
  const freeTimes = useAtomValue(freeTimesAtom);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // 保持上一次的数据，避免更新时闪动
  const [previousData, setPreviousData] = useState<CardRummyItem[]>([]);

  // 使用 useRummyRecommend hook 获取数据（POST）
  const { mutate: fetchRummyRecommend, data: rummyRecommendData, isPending } = useRummyRecommend({
    mutationConfig: {
      retry: false,
      onSuccess: () => {
        setHasLoadedOnce(true);
      },
    },
  });

  // freeTimes / 激活状态变化时重新请求
  useEffect(() => {
    if (!isActive) return;
    fetchRummyRecommend({ params: { free_match_times: freeTimes } });
  }, [isActive, freeTimes, fetchRummyRecommend]);

  // 解析 rummy_recommend 数据（可能是 JSON 字符串）
  const parsedData = useMemo(() => {
    if (!rummyRecommendData?.rummy_recommend) {
      return null;
    }
    try {
      const parsed = typeof rummyRecommendData.rummy_recommend === 'string'
        ? JSON.parse(rummyRecommendData.rummy_recommend)
        : rummyRecommendData.rummy_recommend;
      return Array.isArray(parsed) ? parsed as CardRummyItem[] : null;
    } catch (error) {
      console.error('Failed to parse rummy_recommend:', error);
      return null;
    }
  }, [rummyRecommendData]);

  // 更新 previousData，保持旧数据直到新数据到达
  useEffect(() => {
    if (parsedData && parsedData.length > 0) {
      setPreviousData(parsedData);
    }
  }, [parsedData]);

  // 使用新数据，如果没有则使用旧数据（避免更新时闪动）
  const rummyData = useMemo(() => {
    return parsedData && parsedData.length > 0 ? parsedData : previousData;
  }, [parsedData, previousData]);
  const t = useTranslations('home');
  const router = useRouter();
  const [rummySubPage, setRummySubPage] = useAtom(rummySubPageAtom);
  const [, setRummyGameUrl] = useAtom(rummyGameUrlAtom);
  const isRummyLoadedOnce = useAtomValue(isRummyLoadedOnceAtom);

  // 切换到 Rummy 路由
  const handleSwitchTab = () => {
    router.push(`${menuTabUrl.rummy}?trackSource=rummy`);
  };

  const handleViewAll = useFn(() => {
    track('container_button_click', {
      current_page: 'Home',
      container_name: 'rummy',
      button_name: 'View All'
    });
  });

  const CountdownTimer = ({ endTime }: { endTime: string | number; }) => {
    const leftTime = useMemo(() => {
      const endTimestamp = Number(endTime);
      const currentTimestamp = Date.now() / 1000;
      return Math.max(0, endTimestamp - currentTimestamp);
    }, [endTime]);

    const [, formattedRes] = useCountDown({
      leftTime: leftTime * 1000, // useCountDown expects milliseconds
      onEnd: () => {
        // 倒计时结束，重新请求推荐数据
        if (isActive) {
          fetchRummyRecommend({ params: { free_match_times: freeTimes } });
        }
      },
    });

    const { minutes, seconds } = formattedRes;

    return (
      <div className="flex items-center justify-center text-ss text-tertiary min-w-[93px]">
        {inViewport && (
          <Image src={iconMiaobiaoImg} alt='iconMiaobiao' height={12} className="mr-[3px]" />
        )}
        {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
      </div>
    );
  };

  const isLessThanMinTime = useCallback((timestamp: number) => {
    const MIN_TIME = 5 * 60; // 5分钟
    const endTimestamp = Number(timestamp);
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return Math.max(0, endTimestamp - currentTimestamp) <= MIN_TIME;
  }, []);

  // 格式化时间,输出如：08:30 PM Oct10
  const formatCustomTime = (timestamp: number) => {
    const date = dayjs(timestamp * 1000); // 将秒级时间戳转换为毫秒
    const timePart = date.format('hh:mm A');
    const monthPart = date.format('MMM');
    const dayPart = date.format('D');
    return timePart + ' ' + monthPart + dayPart;
  };

  const handleJoin = useCallback((item: CardRummyItem) => {
    handleSwitchTab();
    const from = rummySubPage.from || '';
    // 如果不是从推荐列表进来的，并且没有加载过一次，则清空游戏url
    if (from !== 'rummy_recommend_list' && !isRummyLoadedOnce) {
      setRummyGameUrl('');
    }
    if (item?.recommend_type === 'match') {
      // 设置跳转参数
      setRummySubPage({
        from: from,
        subPage: RummySubPage.RUMMY_MATCH,
        gameId: Number(item?.game_id),
        matchConfigId: Number(item?.match_config_id),
        autoPlay: 1,
      });
    } else {
      setRummySubPage({
        from: from,
        subPage: RummySubPage.RUMMY_CASH,
        gameId: Number(item?.game_id),
        level: Number(item?.level),
        seatCount: Number(item?.seat_count),
        autoPlay: 1,
      });
    }
  }, [rummySubPage, isRummyLoadedOnce, setRummySubPage, setRummyGameUrl]);

  // 使用 useMemo 计算 loading 状态，只在第一次加载时显示
  const isLoading = useMemo(() => {
    return isPending && !hasLoadedOnce;
  }, [isPending, hasLoadedOnce]);

  // 如果没有数据且不在加载中，不渲染
  if (!isLoading && (!rummyData || rummyData.length === 0)) {
    return null;
  }

  return (
    <>
      <div ref={containerRef} className="mt-[17px] pl-[12px]">
        <TitleBar
          colors={['#FF613B', '#E8370D']}
          leftTitle={'Rummy'}
          rightTitle={
            <Link href={`${menuTabUrl.rummy}?trackSource=rummy`} className="text-accent-blue text-s font-medium">
              {t('viewAll')}
            </Link>
          }
          onClickRightTitle={handleViewAll}
        />
        <div className="flex flex-col gap-[10px] pr-[12px]">
          {isLoading ? (
            <Spin className="w-full h-[200px]"></Spin>
          ) : (
            rummyData.map((item, index) => {
              // 生成唯一 key：match 类型使用 match_config_id，cash 类型使用 game_id + level
              const uniqueKey = item?.recommend_type === 'match'
                ? `match-${item.match_config_id || item.game_id}-${index}`
                : `cash-${item.game_id}-${item.level || 0}-${index}`;

              return (
                <RummyCardWithExposure
                  key={uniqueKey}
                  item={item}
                  index={index}
                >
                  {({ cardRef, handleClick }) => {
                    const handleJoinWithTracking = () => {
                      handleClick();
                      handleJoin(item);
                    };

                    return (
                      <div ref={cardRef} key={uniqueKey} className="relative bg-surface rounded-[10px] px-[15px] py-[19px]">
                        {item?.recommend_type !== 'match' && (
                          <div className="min-w-[59px] absolute top-0 right-0 bg-selected text-center text-secondary text-[10px] font-medium px-[10px] h-[15px] leading-[15px]"
                            style={{
                              borderRadius: '0px 10px 0px 10px',
                            }}
                          >{item.game_type?.includes('Points') ? 'Points' : 'Deals'}</div>
                        )}
                        {item?.recommend_type === 'match' ? (
                          <>
                            <div className="min-w-[59px] absolute top-0 right-0 bg-selected text-center text-secondary text-[10px] font-medium px-[10px] h-[15px] leading-[15px]"
                              style={{
                                borderRadius: '0px 10px 0px 10px',
                              }}
                            >{'Tournament'}</div>

                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <div className="flex items-center leading-ss">
                                  <div className="text-white text-sm font-semibold break-all pr-3">
                                    {item?.match_name}
                                  </div>
                                </div>
                                <div className="text-secondary text-xss font-medium mt-2 px-[6px] h-[18px] rounded-[11px] flex items-center justify-center bg-[rgba(42,53,70,0.6)] w-fit">
                                  {t('rewards')} ₹{item?.total_reward}
                                </div>
                              </div>
                              {/* 人满赛和定时赛 */}
                              {
                                item.match_form === 3 ? (
                                  <Button variant="warning" size="sm" className="w-[93px] mt-1"
                                    onClick={() => handleJoinWithTracking()}>
                                    ₹{item?.fee_num}
                                  </Button>) : (
                                  <Button variant="success" size="sm" className="w-[93px] mt-1"
                                    onClick={() => handleJoinWithTracking()}>
                                    {t('free')}
                                  </Button>)
                              }
                            </div>

                            <div className="flex items-center justify-between absolute leading-3 right-[22px] bottom-[6px]">
                              {/* 人满赛 */}
                              {
                                item.match_type === 2 ? (
                                  <div className="text-tertiary text-[10px] font-medium">{t('startsWhenFull')}</div>
                                ) : <div className="text-secondary text-[10px] font-medium">{isLessThanMinTime(item.match_begin_time) ? <CountdownTimer endTime={item?.match_begin_time} /> : formatCustomTime(item?.match_begin_time)}</div>
                              }
                            </div>
                          </>
                        ) : <>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                              <div className="text-white text-sm font-semibold">Entry: ₹{item.game_type?.includes('Points') ? item?.entry_min : item?.base_ante}</div>
                              <div className="text-secondary text-xss font-medium mt-2 px-[6px] h-[18px] rounded-[11px] flex items-center justify-center bg-[rgba(42,53,70,0.6)] w-fit">
                                {item.game_type?.includes('Points') ? `1 Point = ₹${item?.base_ante}` : `${item?.game_type}`}
                              </div>
                            </div>

                            <Button variant="primary" size="sm" className="w-[93px] mt-1"
                              onClick={() => handleJoinWithTracking()}
                            >
                              {t('join')}
                            </Button>
                          </div>
                        </>}
                      </div>
                    );
                  }}
                </RummyCardWithExposure>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};
